import { getStores } from '../services/storage';
import { UIKey } from '../services/storage/ui-key-value';
import { uiKeyToParamKey } from './uikey';

const UTM_UI_KEYS = [
  UIKey.UTM_SOURCE,
  UIKey.UTM_MEDIUM,
  UIKey.UTM_CAMPAIGN,
  UIKey.UTM_TERM,
  UIKey.GCLID,
  UIKey.FBCLID,
  UIKey.LI_FAT_ID,
] as const;

export function getUtmKey(key: UIKey): string {
  const storeKeyRaw = getStores().uiKeyValue.getState().getFromStore(key);
  const storeKey = typeof storeKeyRaw !== 'string' ? '' : storeKeyRaw;

  const params = new URLSearchParams(window.location.search);
  const paramKey = uiKeyToParamKey(key);
  let paramValue = '';
  if (paramKey.length > 0) {
    paramValue = params.get(paramKey) ?? paramValue;
  }

  return paramValue.length > 0 && paramValue !== 'unknown' ? paramValue : storeKey;
}

export function storeUtmKey(key: UIKey, value?: string) {
  let parsedValue = value ?? '';
  if (parsedValue.length === 0 || parsedValue === 'unknown') {
    parsedValue = getUtmKey(key);
  }

  if (parsedValue.length === 0 || parsedValue === 'unknown') {
    return;
  }

  getStores().uiKeyValue.getState().setToStore(key, parsedValue);
}

/**
 * Merges stored/URL UTMs onto a path or absolute URL (adds only missing utm_* keys).
 * Use for Clerk redirect URLs and other plain href strings.
 * For TanStack Router `Link` / `navigate` / `redirect`, use {@link tanstackLocationWithUtm} instead:
 * a `to` value that includes `?…` does not match file routes (e.g. `/auth/signup`) and triggers notFound → `/app`.
 * REF: https://developer.mozilla.org/en-US/docs/Web/API/URL/URL — relative paths need a base.
 */
export function appendUtmQueryString(pathOrAbsoluteUrl: string): string {
  if (pathOrAbsoluteUrl.length === 0) {
    return pathOrAbsoluteUrl;
  }

  try {
    const base =
      typeof window !== 'undefined' && window.location.origin.length > 0
        ? window.location.origin
        : 'http://localhost';
    const url = pathOrAbsoluteUrl.includes('://')
      ? new URL(pathOrAbsoluteUrl)
      : new URL(pathOrAbsoluteUrl, base);

    for (const uiKey of UTM_UI_KEYS) {
      const paramName = uiKeyToParamKey(uiKey);
      if (paramName.length === 0) {
        continue;
      }
      const existing = url.searchParams.get(paramName);
      if (existing != null && existing.length > 0 && existing !== 'unknown') {
        continue;
      }
      const v = getUtmKey(uiKey);
      if (v.length > 0 && v !== 'unknown') {
        url.searchParams.set(paramName, v);
      }
    }

    const qs = url.searchParams.toString();
    const suffix = qs.length > 0 ? `?${qs}` : '';

    if (pathOrAbsoluteUrl.includes('://')) {
      return `${url.origin}${url.pathname}${suffix}`;
    }

    return `${url.pathname}${suffix}`;
  } catch {
    return pathOrAbsoluteUrl;
  }
}

/**
 * Pathname + search for TanStack Router (`Link`, `navigate`, `redirect`).
 * REF: https://tanstack.com/router/latest/docs/framework/react/guide/navigation — `to` is the path; search is separate.
 */
export function tanstackLocationWithUtm(path: string): {
  to: string;
  search: Record<string, string>;
} {
  const full = appendUtmQueryString(path);
  const base =
    typeof window !== 'undefined' && window.location.origin.length > 0
      ? window.location.origin
      : 'http://localhost';
  const url = new URL(full, base);
  return {
    to: url.pathname,
    search: Object.fromEntries(url.searchParams.entries()),
  };
}
